import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtCurrency, STATUS_COLORS } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, PencilSimple, Trash, MagnifyingGlass } from "@phosphor-icons/react";

function optionsFor(field, lists) {
  const raw = field.options || lists[field.optionsKey] || [];
  return raw.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
}

function isOverdue(dateStr) {
  if (!dateStr) return false;
  return String(dateStr).slice(0, 10) < new Date().toISOString().slice(0, 10);
}

export default function CrudTable({ resource, config, lists, nameMap, onData, extraHeader }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get(`/data/${resource}`);
    setRows(res.data);
    onData?.(res.data);
    setLoading(false);
  }, [resource, onData]);

  useEffect(() => { load(); }, [load]);

  const visibleFields = config.fields.filter((f) => !f.hideInTable);

  const openAdd = () => {
    const init = {};
    config.fields.forEach((f) => {
      if (f.type === "date") init[f.key] = new Date().toISOString().slice(0, 10);
    });
    setForm(init);
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setForm({ ...row });
    setEditing(row.id);
    setDialogOpen(true);
  };

  const save = async () => {
    const missing = config.fields.find((f) => f.required && !String(form[f.key] ?? "").trim());
    if (missing) { toast.error(`${missing.label} is required`); return; }
    try {
      if (editing) {
        await api.put(`/data/${resource}/${editing}`, form);
        toast.success("Updated successfully");
      } else {
        await api.post(`/data/${resource}`, form);
        toast.success("Added successfully");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/data/${resource}/${deleteId}`);
      toast.success("Deleted");
      setDeleteId(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const renderCell = (row, f) => {
    let val = row[f.key];
    if (f.team) return nameMap?.[val] || val || "—";
    if (f.type === "currency") return <span className="tabular-nums">{fmtCurrency(val)}</span>;
    if (f.type === "percent") return val != null ? `${val}%` : "—";
    if (f.badge && val) {
      const cls = STATUS_COLORS[val] || "text-slate bg-slate/10";
      return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{val}</span>;
    }
    if (f.followup && val) {
      const overdue = isOverdue(val) && (row.counselling_booked === "No" || row.status !== "Active");
      return <span className={overdue ? "text-rose font-semibold" : ""}>{val}{overdue ? " ⚠" : ""}</span>;
    }
    return val ?? "—";
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return config.fields.some((f) => {
      const v = f.team ? nameMap?.[r[f.key]] : r[f.key];
      return String(v ?? "").toLowerCase().includes(s);
    });
  });

  return (
    <div className="space-y-4">
      {extraHeader}
      <div className="bg-white rounded-lg border border-slate-200">
        <div className="flex items-center justify-between gap-4 p-4 border-b border-slate-200">
          <div className="relative w-full max-w-xs">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate" />
            <Input
              data-testid={`${resource}-search`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="pl-9 h-9 focus-visible:ring-gold"
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate">{filtered.length} records</span>
            <Button data-testid={`${resource}-add-button`} onClick={openAdd} className="bg-navy hover:bg-navy/90 text-white gap-1.5">
              <Plus size={16} weight="bold" /> Add New
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {visibleFields.map((f) => (
                  <TableHead key={f.key} className="text-[11px] uppercase tracking-wider text-slate font-semibold whitespace-nowrap">
                    {f.label}
                  </TableHead>
                ))}
                <TableHead className="text-right text-[11px] uppercase tracking-wider text-slate font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={visibleFields.length + 1} className="text-center py-10 text-slate">Loading…</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={visibleFields.length + 1} className="text-center py-10 text-slate">No records yet. Click "Add New" to start.</TableCell></TableRow>
              ) : (
                filtered.map((row) => (
                  <TableRow key={row.id} data-testid={`${resource}-row`} className="hover:bg-slate-50">
                    {visibleFields.map((f) => (
                      <TableCell key={f.key} className={`text-sm whitespace-nowrap ${f.type === "currency" || f.type === "percent" ? "text-right" : ""}`}>
                        {renderCell(row, f)}
                      </TableCell>
                    ))}
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button data-testid={`${resource}-edit`} onClick={() => openEdit(row)} className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate hover:text-navy">
                          <PencilSimple size={16} />
                        </button>
                        <button data-testid={`${resource}-delete`} onClick={() => setDeleteId(row.id)} className="h-8 w-8 rounded-md hover:bg-rose/10 flex items-center justify-center text-slate hover:text-rose">
                          <Trash size={16} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : "Add"} {config.title} Record</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {config.fields.map((f) => (
              <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                <Label className="text-xs text-slate mb-1.5 block">{f.label}{f.required && <span className="text-rose">*</span>}{f.computed ? " (auto)" : ""}</Label>
                {f.computed ? (
                  <div className="h-10 px-3 flex items-center rounded-md bg-slate-100 text-sm text-slate">
                    {f.type === "currency" ? fmtCurrency(form[f.key]) : (form[f.key] ?? "—")}{f.type === "percent" ? "%" : ""}
                  </div>
                ) : f.type === "select" ? (
                  <Select value={form[f.key] ? String(form[f.key]) : undefined} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger data-testid={`field-${f.key}`} className="focus:ring-gold"><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
                    <SelectContent>
                      {optionsFor(f, lists).map((o) => (
                        <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "textarea" ? (
                  <Textarea data-testid={`field-${f.key}`} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="focus-visible:ring-gold" rows={2} />
                ) : (
                  <Input
                    data-testid={`field-${f.key}`}
                    type={f.type === "date" ? "date" : (f.type === "number" || f.type === "currency") ? "number" : "text"}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: (f.type === "number" || f.type === "currency") ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value })}
                    className="focus-visible:ring-gold"
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-record-button" onClick={save} className="bg-navy hover:bg-navy/90 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-button" onClick={doDelete} className="bg-rose hover:bg-rose/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
